import React, { useContext, useLayoutEffect, useRef, useState } from "react";
import Draggable from "react-draggable";
import { Trans } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Link, Typography } from "@mui/material";
import { ThemeWrapper } from "@verity/components";
import { CoreIconButton, CoreIconButtonProps, coreToast } from "@verity/components/core";
import { PortalControllerContext } from "../../context";
import { PORTAL_TOAST_CONTAINER_ID } from "../../shared";
import i18n from "../../../i18n";
import CornersOutIcon from "../../../images/portal/cornersOut.svg?react";
import CloseXIcon from "../../../images/portal/closeX.svg?react";
import EmptyCheckCircleIcon from "../../../images/portal/emptyCheckCircle.svg?react";
import Info from "../../../images/portal/info.svg?react";
import GreenCheckCircleIcon from "../../../images/portal/greenCheckCircle.svg?react";
import ListNumbersIcon from "../../../images/portal-navigation/listNumbers.svg?react";
import MinimizeIcon from "../../../images/portal/minimize.svg?react";
import styles from "./index.module.css";

type IntegrationButtonProps = {
	text: string;
	completed: boolean;
	selected: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

function MinimizedButton({ children, ...buttonProps }: CoreIconButtonProps) {
	return (
		<CoreIconButton
			{...buttonProps}
			variant="plain"
			slotProps={{ tooltip: { placement: "top" } }}
			sx={{
				borderRadius: "100%",
				backgroundColor: "#996ae1",
				"&&&&:hover": {
					backgroundColor: "#7542c1",
				},
				"&&&&:active": {
					backgroundColor: "#612bb2",
				},
			}}
		>
			{children}
		</CoreIconButton>
	);
}

function IntegrationButton({ text, completed, selected, ...buttonProps }: IntegrationButtonProps) {
	return (
		<button
			{...buttonProps}
			className={`${styles.integrationButton} ${selected ? styles.integrationButtonSelected : undefined}`}
		>
			{text}
			{completed ? <GreenCheckCircleIcon /> : <EmptyCheckCircleIcon />}
		</button>
	);
}

export default function QuickSetupGuide() {
	const { apiIntegrations, currPage, navigateToPage, portalContext, visualizerDetected, lti1p3Active, dismissUiElement } =
		useContext(PortalControllerContext);
	const { lms } = portalContext;
	const { state } = useLocation();

	const [position, setPosition] = useState({ x: 0, y: 0 });
	const [minimized, setMinimized] = useState(false);

	const containerFullRef = useRef<HTMLDivElement>(null);
	const containerMinimizedRef = useRef<HTMLDivElement>(null);
	const wrapperFullRef = useRef<HTMLDivElement>(null);
	const wrapperMinimizedRef = useRef<HTMLDivElement>(null);

	const showVisualizerStep = lms !== "BlackboardLearn";
	const showApiStep = lms === "moodle";
	const lti1p3Selected = currPage === "integrationSettings" && state?.selected === "LTI 1.3";
	const visualizerSelected = currPage === "integrationSettings" && state?.selected === "LMS Visualizer";
	const apiSelected = currPage === "integrationSettings" && state?.selected === "LMS API";
	const hasMoodleApiToken = lms === "moodle" && apiIntegrations?.institute?.edit;

	useLayoutEffect(() => {
		const handleBounds = () => {
			const activeContainer = minimized ? containerMinimizedRef.current : containerFullRef.current;
			const activeWrapper = minimized ? wrapperMinimizedRef.current : wrapperFullRef.current;

			if (!activeContainer || !activeWrapper) return;

			const containerWidth = activeContainer.offsetWidth;
			const containerHeight = activeContainer.offsetHeight;
			const wrapperWidth = activeWrapper.offsetWidth;
			const wrapperHeight = activeWrapper.offsetHeight;

			const minX = wrapperWidth - containerWidth;
			const minY = wrapperHeight - containerHeight;

			setPosition((pos) => {
				const newX = Math.min(Math.max(minX, pos.x), 0);
				const newY = Math.min(Math.max(minY, pos.y), 0);

				return { x: newX, y: newY };
			});
		};

		window.addEventListener("resize", handleBounds);
		handleBounds();

		return () => window.removeEventListener("resize", handleBounds);
	}, [minimized]);

	return (
		<ThemeWrapper>
			<div className={styles.guideContainer}>
				<div ref={containerFullRef} className={styles.guideWrapperFull}>
					<Draggable
						nodeRef={wrapperFullRef}
						bounds="parent"
						handle={`.${styles.guideFullHeader}`}
						position={position}
						onDrag={(_, data) => setPosition({ x: data.x, y: data.y })}
					>
						<div ref={wrapperFullRef} className={`${styles.guideFull} ${!minimized ? styles.guideFullOpaque : ""}`}>
							<div className={styles.guideFullHeader}>
								<Typography fontSize="14px" fontWeight="600" className={styles.guideFullTitle}>
									{i18n.t("portal.components.quickSetupGuideTSX.quickSetupGuide")}
								</Typography>
								<div className={styles.guideFullButtons}>
									<CoreIconButton
										variant="plain"
										className={styles.guideFullButton}
										tooltip={i18n.t("portal.components.quickSetupGuideTSX.full.minimize")}
										onClick={() => setMinimized(true)}
										slotProps={{ tooltip: { placement: "bottom" } }}
									>
										<MinimizeIcon />
									</CoreIconButton>
									<CoreIconButton
										variant="plain"
										className={styles.guideFullButton}
										tooltip={i18n.t("portal.components.quickSetupGuideTSX.full.close")}
										onClick={() => {
											dismissUiElement("quickSetupGuide");
											coreToast.info(i18n.t("portal.components.quickSetupGuideTSX.closeToast"), {
												containerId: PORTAL_TOAST_CONTAINER_ID,
											});
										}}
										slotProps={{ tooltip: { placement: "bottom" } }}
									>
										<CloseXIcon />
									</CoreIconButton>
								</div>
							</div>
							<div className={styles.guideFullBody}>
								<Typography fontSize="14px">
									{i18n.t("portal.components.quickSetupGuideTSX.full.bodyPrompt")}
								</Typography>
								<div className={styles.guideFullIntegrations}>
									<IntegrationButton
										text={i18n.t("portal.components.quickSetupGuideTSX.full.integrations.1p3")}
										completed={!!lti1p3Active}
										selected={lti1p3Selected}
										onClick={() => navigateToPage("integrationSettings", { selected: "LTI 1.3" })}
									/>
									{showVisualizerStep && (
										<IntegrationButton
											text={i18n.t("portal.components.quickSetupGuideTSX.full.integrations.visualizer")}
											completed={!!visualizerDetected}
											selected={visualizerSelected}
											onClick={() => navigateToPage("integrationSettings", { selected: "LMS Visualizer" })}
										/>
									)}
									{showApiStep && (
										<IntegrationButton
											text={i18n.t("portal.components.quickSetupGuideTSX.full.integrations.api")}
											completed={!!hasMoodleApiToken}
											selected={apiSelected}
											onClick={() => navigateToPage("integrationSettings", { selected: "LMS API" })}
										/>
									)}
								</div>
								<div className={styles.guideFullFooter}>
									<Info role="presentation" className={styles.guideFullFooterIcon} />
									<Trans
										i18nKey="portal.components.quickSetupGuideTSX.full.footerLink"
										components={{
											a: <Link href="https://support.yuja.com/" target="_blank" rel="noreferrer" fontSize="12px" />,
										}}
									/>
								</div>
							</div>
						</div>
					</Draggable>
				</div>
				<div ref={containerMinimizedRef} className={styles.guideWrapperMinimized}>
					<Draggable
						nodeRef={wrapperMinimizedRef}
						bounds="parent"
						position={position}
						onDrag={(_, data) => setPosition({ x: data.x, y: data.y })}
					>
						<div
							role="button"
							tabIndex={0}
							ref={wrapperMinimizedRef}
							className={`${styles.guideMinimized} ${minimized ? styles.guideMinimizedOpaque : ""}`}
						>
							<div className={styles.guideMinimizedHandle}>
								<ListNumbersIcon />
								<Typography fontSize="16px" fontWeight="600">
									{i18n.t("portal.components.quickSetupGuideTSX.setupGuide")}
								</Typography>
							</div>
							<div className={styles.guideMinimizedButtons}>
								<MinimizedButton
									className={styles.guideMinimizedButton}
									tooltip={i18n.t("portal.components.quickSetupGuideTSX.minimized.expand")}
									onClick={() => setMinimized(false)}
								>
									<CornersOutIcon />
								</MinimizedButton>
								<MinimizedButton
									className={styles.guideMinimizedButton}
									tooltip={i18n.t("portal.components.quickSetupGuideTSX.minimized.close")}
									onClick={() => {
										dismissUiElement("quickSetupGuide");
										coreToast.info(i18n.t("portal.components.quickSetupGuideTSX.closeToast"), {
											containerId: PORTAL_TOAST_CONTAINER_ID,
										});
									}}
								>
									<CloseXIcon />
								</MinimizedButton>
							</div>
						</div>
					</Draggable>
				</div>
			</div>
		</ThemeWrapper>
	);
}
